import { Component, OnInit } from '@angular/core';

interface CityOption { key: string; labelKey: string; countryKey: string; flag: string; }
interface DayForecast { dayKey: string; icon: string; conditionKey: string; high: number; low: number; rain: number; }
interface CityWeather {
    cityKey: string;
    now: { temp: number; icon: string; conditionKey: string; humidity: number; wind: number; rain: number; };
    days: DayForecast[];
}
interface Advisory { icon: string; titleKey: string; textKey: string; tone: 'green' | 'amber' | 'blue'; }

@Component({
    selector: 'app-weather',
    templateUrl: './weather.component.html',
    styleUrls: ['./weather.component.css'],
    standalone: false
})
export class WeatherComponent implements OnInit {
    city = 'yaounde';

    // Maps raw API/day-index conditions text (English, from backend) to translation slugs.
    // Extend this if the API introduces new condition strings.
    private readonly conditionSlugMap: Record<string, string> = {
        'Partly cloudy': 'partly_cloudy',
        'Showers': 'showers',
        'Thunderstorms': 'thunderstorms',
        'Rain': 'rain',
        'Sunny': 'sunny',
        'Heavy rain': 'heavy_rain',
        'Hot & sunny': 'hot_sunny',
        'Light rain': 'light_rain',
    };

    private readonly dayKeyMap: Record<string, string> = {
        Sat: 'sat', Sun: 'sun', Mon: 'mon', Tue: 'tue', Wed: 'wed', Thu: 'thu', Fri: 'fri',
    };

    readonly cities: CityOption[] = [
        { key: 'yaounde', labelKey: 'weather_page.cities.yaounde', countryKey: 'market_prices_page.countries.cameroon', flag: '🇨🇲' },
        { key: 'douala', labelKey: 'weather_page.cities.douala', countryKey: 'market_prices_page.countries.cameroon', flag: '🇨🇲' },
        { key: 'garoua', labelKey: 'weather_page.cities.garoua', countryKey: 'market_prices_page.countries.cameroon', flag: '🇨🇲' },
        { key: 'ndjamena', labelKey: 'weather_page.cities.ndjamena', countryKey: 'market_prices_page.countries.chad', flag: '🇹🇩' },
        { key: 'bangui', labelKey: 'weather_page.cities.bangui', countryKey: 'market_prices_page.countries.car', flag: '🇨🇫' },
        { key: 'brazzaville', labelKey: 'weather_page.cities.brazzaville', countryKey: 'market_prices_page.countries.congo', flag: '🇨🇬' },
        { key: 'libreville', labelKey: 'weather_page.cities.libreville', countryKey: 'market_prices_page.countries.gabon', flag: '🇬🇦' },
        { key: 'malabo', labelKey: 'weather_page.cities.malabo', countryKey: 'market_prices_page.countries.eq_guinea', flag: '🇬🇶' },
    ];

    weather: Record<string, CityWeather> = {
        yaounde: {
            cityKey: 'weather_page.cities.yaounde',
            now: { temp: 24, icon: '⛅', conditionKey: 'weather_page.conditions.partly_cloudy', humidity: 78, wind: 9, rain: 40 },
            days: [
                { dayKey: 'weather_page.days.sat', icon: '⛅', conditionKey: 'weather_page.conditions.partly_cloudy', high: 27, low: 19, rain: 40 },
                { dayKey: 'weather_page.days.sun', icon: '🌧️', conditionKey: 'weather_page.conditions.showers', high: 25, low: 19, rain: 70 },
                { dayKey: 'weather_page.days.mon', icon: '⛈️', conditionKey: 'weather_page.conditions.thunderstorms', high: 24, low: 18, rain: 85 },
                { dayKey: 'weather_page.days.tue', icon: '🌧️', conditionKey: 'weather_page.conditions.rain', high: 25, low: 19, rain: 65 },
                { dayKey: 'weather_page.days.wed', icon: '⛅', conditionKey: 'weather_page.conditions.partly_cloudy', high: 26, low: 19, rain: 35 },
                { dayKey: 'weather_page.days.thu', icon: '☀️', conditionKey: 'weather_page.conditions.sunny', high: 28, low: 20, rain: 10 },
                { dayKey: 'weather_page.days.fri', icon: '⛅', conditionKey: 'weather_page.conditions.partly_cloudy', high: 27, low: 20, rain: 30 },
            ],
        },
        douala: {
            cityKey: 'weather_page.cities.douala',
            now: { temp: 27, icon: '🌧️', conditionKey: 'weather_page.conditions.light_rain', humidity: 88, wind: 12, rain: 75 },
            days: [
                { dayKey: 'weather_page.days.sat', icon: '🌧️', conditionKey: 'weather_page.conditions.rain', high: 28, low: 23, rain: 75 },
                { dayKey: 'weather_page.days.sun', icon: '⛈️', conditionKey: 'weather_page.conditions.thunderstorms', high: 27, low: 23, rain: 90 },
                { dayKey: 'weather_page.days.mon', icon: '🌧️', conditionKey: 'weather_page.conditions.heavy_rain', high: 26, low: 22, rain: 95 },
                { dayKey: 'weather_page.days.tue', icon: '🌧️', conditionKey: 'weather_page.conditions.rain', high: 27, low: 23, rain: 80 },
                { dayKey: 'weather_page.days.wed', icon: '🌧️', conditionKey: 'weather_page.conditions.showers', high: 28, low: 23, rain: 60 },
                { dayKey: 'weather_page.days.thu', icon: '⛅', conditionKey: 'weather_page.conditions.partly_cloudy', high: 29, low: 24, rain: 40 },
                { dayKey: 'weather_page.days.fri', icon: '🌧️', conditionKey: 'weather_page.conditions.showers', high: 28, low: 23, rain: 55 },
            ],
        },
        garoua: {
            cityKey: 'weather_page.cities.garoua',
            now: { temp: 33, icon: '☀️', conditionKey: 'weather_page.conditions.sunny', humidity: 52, wind: 15, rain: 15 },
            days: [
                { dayKey: 'weather_page.days.sat', icon: '☀️', conditionKey: 'weather_page.conditions.sunny', high: 35, low: 24, rain: 10 },
                { dayKey: 'weather_page.days.sun', icon: '☀️', conditionKey: 'weather_page.conditions.sunny', high: 36, low: 25, rain: 5 },
                { dayKey: 'weather_page.days.mon', icon: '⛅', conditionKey: 'weather_page.conditions.partly_cloudy', high: 34, low: 24, rain: 25 },
                { dayKey: 'weather_page.days.tue', icon: '⛈️', conditionKey: 'weather_page.conditions.thunderstorms', high: 32, low: 23, rain: 65 },
                { dayKey: 'weather_page.days.wed', icon: '🌧️', conditionKey: 'weather_page.conditions.showers', high: 31, low: 23, rain: 55 },
                { dayKey: 'weather_page.days.thu', icon: '⛅', conditionKey: 'weather_page.conditions.partly_cloudy', high: 33, low: 24, rain: 30 },
                { dayKey: 'weather_page.days.fri', icon: '☀️', conditionKey: 'weather_page.conditions.sunny', high: 35, low: 24, rain: 10 },
            ],
        },
        ndjamena: {
            cityKey: 'weather_page.cities.ndjamena',
            now: { temp: 36, icon: '☀️', conditionKey: 'weather_page.conditions.hot_sunny', humidity: 41, wind: 18, rain: 5 },
            days: [
                { dayKey: 'weather_page.days.sat', icon: '☀️', conditionKey: 'weather_page.conditions.sunny', high: 38, low: 26, rain: 5 },
                { dayKey: 'weather_page.days.sun', icon: '☀️', conditionKey: 'weather_page.conditions.sunny', high: 39, low: 27, rain: 5 },
                { dayKey: 'weather_page.days.mon', icon: '⛅', conditionKey: 'weather_page.conditions.partly_cloudy', high: 37, low: 26, rain: 20 },
                { dayKey: 'weather_page.days.tue', icon: '⛅', conditionKey: 'weather_page.conditions.partly_cloudy', high: 36, low: 26, rain: 25 },
                { dayKey: 'weather_page.days.wed', icon: '⛈️', conditionKey: 'weather_page.conditions.thunderstorms', high: 34, low: 25, rain: 60 },
                { dayKey: 'weather_page.days.thu', icon: '🌧️', conditionKey: 'weather_page.conditions.showers', high: 33, low: 24, rain: 50 },
                { dayKey: 'weather_page.days.fri', icon: '☀️', conditionKey: 'weather_page.conditions.sunny', high: 36, low: 25, rain: 10 },
            ],
        },
        bangui: {
            cityKey: 'weather_page.cities.bangui',
            now: { temp: 26, icon: '⛈️', conditionKey: 'weather_page.conditions.thunderstorms', humidity: 84, wind: 8, rain: 80 },
            days: [
                { dayKey: 'weather_page.days.sat', icon: '⛈️', conditionKey: 'weather_page.conditions.thunderstorms', high: 28, low: 21, rain: 80 },
                { dayKey: 'weather_page.days.sun', icon: '🌧️', conditionKey: 'weather_page.conditions.rain', high: 27, low: 21, rain: 70 },
                { dayKey: 'weather_page.days.mon', icon: '⛅', conditionKey: 'weather_page.conditions.partly_cloudy', high: 29, low: 21, rain: 35 },
                { dayKey: 'weather_page.days.tue', icon: '🌧️', conditionKey: 'weather_page.conditions.showers', high: 28, low: 21, rain: 60 },
                { dayKey: 'weather_page.days.wed', icon: '⛈️', conditionKey: 'weather_page.conditions.thunderstorms', high: 27, low: 21, rain: 85 },
                { dayKey: 'weather_page.days.thu', icon: '🌧️', conditionKey: 'weather_page.conditions.rain', high: 27, low: 21, rain: 65 },
                { dayKey: 'weather_page.days.fri', icon: '⛅', conditionKey: 'weather_page.conditions.partly_cloudy', high: 29, low: 22, rain: 30 },
            ],
        },
        brazzaville: {
            cityKey: 'weather_page.cities.brazzaville',
            now: { temp: 25, icon: '⛅', conditionKey: 'weather_page.conditions.partly_cloudy', humidity: 72, wind: 10, rain: 20 },
            days: [
                { dayKey: 'weather_page.days.sat', icon: '⛅', conditionKey: 'weather_page.conditions.partly_cloudy', high: 27, low: 19, rain: 20 },
                { dayKey: 'weather_page.days.sun', icon: '☀️', conditionKey: 'weather_page.conditions.sunny', high: 28, low: 19, rain: 10 },
                { dayKey: 'weather_page.days.mon', icon: '☀️', conditionKey: 'weather_page.conditions.sunny', high: 29, low: 20, rain: 5 },
                { dayKey: 'weather_page.days.tue', icon: '⛅', conditionKey: 'weather_page.conditions.partly_cloudy', high: 28, low: 20, rain: 25 },
                { dayKey: 'weather_page.days.wed', icon: '⛅', conditionKey: 'weather_page.conditions.partly_cloudy', high: 27, low: 19, rain: 30 },
                { dayKey: 'weather_page.days.thu', icon: '🌧️', conditionKey: 'weather_page.conditions.showers', high: 26, low: 19, rain: 55 },
                { dayKey: 'weather_page.days.fri', icon: '⛅', conditionKey: 'weather_page.conditions.partly_cloudy', high: 27, low: 19, rain: 25 },
            ],
        },
        libreville: {
            cityKey: 'weather_page.cities.libreville',
            now: { temp: 28, icon: '🌧️', conditionKey: 'weather_page.conditions.showers', humidity: 86, wind: 14, rain: 65 },
            days: [
                { dayKey: 'weather_page.days.sat', icon: '🌧️', conditionKey: 'weather_page.conditions.showers', high: 29, low: 23, rain: 65 },
                { dayKey: 'weather_page.days.sun', icon: '⛈️', conditionKey: 'weather_page.conditions.thunderstorms', high: 28, low: 23, rain: 80 },
                { dayKey: 'weather_page.days.mon', icon: '🌧️', conditionKey: 'weather_page.conditions.rain', high: 28, low: 23, rain: 70 },
                { dayKey: 'weather_page.days.tue', icon: '⛅', conditionKey: 'weather_page.conditions.partly_cloudy', high: 29, low: 24, rain: 40 },
                { dayKey: 'weather_page.days.wed', icon: '⛅', conditionKey: 'weather_page.conditions.partly_cloudy', high: 30, low: 24, rain: 35 },
                { dayKey: 'weather_page.days.thu', icon: '🌧️', conditionKey: 'weather_page.conditions.showers', high: 29, low: 23, rain: 60 },
                { dayKey: 'weather_page.days.fri', icon: '🌧️', conditionKey: 'weather_page.conditions.rain', high: 28, low: 23, rain: 70 },
            ],
        },
        malabo: {
            cityKey: 'weather_page.cities.malabo',
            now: { temp: 27, icon: '⛈️', conditionKey: 'weather_page.conditions.thunderstorms', humidity: 90, wind: 11, rain: 85 },
            days: [
                { dayKey: 'weather_page.days.sat', icon: '⛈️', conditionKey: 'weather_page.conditions.thunderstorms', high: 28, low: 23, rain: 85 },
                { dayKey: 'weather_page.days.sun', icon: '🌧️', conditionKey: 'weather_page.conditions.heavy_rain', high: 27, low: 23, rain: 90 },
                { dayKey: 'weather_page.days.mon', icon: '🌧️', conditionKey: 'weather_page.conditions.rain', high: 27, low: 22, rain: 75 },
                { dayKey: 'weather_page.days.tue', icon: '🌧️', conditionKey: 'weather_page.conditions.showers', high: 28, low: 23, rain: 60 },
                { dayKey: 'weather_page.days.wed', icon: '⛈️', conditionKey: 'weather_page.conditions.thunderstorms', high: 27, low: 22, rain: 85 },
                { dayKey: 'weather_page.days.thu', icon: '🌧️', conditionKey: 'weather_page.conditions.rain', high: 27, low: 23, rain: 70 },
                { dayKey: 'weather_page.days.fri', icon: '⛅', conditionKey: 'weather_page.conditions.partly_cloudy', high: 29, low: 23, rain: 40 },
            ],
        },
    };

    readonly advisories: Advisory[] = [
        { icon: '🌽', titleKey: 'weather_page.advisory_1_title', textKey: 'weather_page.advisory_1_text', tone: 'green' },
        { icon: '⚠️', titleKey: 'weather_page.advisory_2_title', textKey: 'weather_page.advisory_2_text', tone: 'amber' },
        { icon: '💧', titleKey: 'weather_page.advisory_3_title', textKey: 'weather_page.advisory_3_text', tone: 'blue' },
    ];

    ngOnInit(): void {
        fetch('/api/weather')
            .then((res) => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
            .then((rows: Array<Record<string, unknown>>) => {
                if (!Array.isArray(rows) || rows.length === 0) return;
                const grouped: Record<string, CityWeather> = {};
                const dayIndices: Record<string, number[]> = {};
                for (const r of rows) {
                    const key = String(r['cityKey'] ?? '');
                    if (!key) continue;
                    if (!grouped[key]) {
                        grouped[key] = {
                            cityKey: `weather_page.cities.${key}`,
                            now: { temp: 0, icon: '⛅', conditionKey: 'weather_page.conditions.partly_cloudy', humidity: 0, wind: 0, rain: 0 },
                            days: [],
                        };
                    }
                    const dayIndex = Number(r['dayIndex'] ?? -1);
                    const rawCondition = String(r['conditions'] ?? '');
                    const conditionSlug = this.conditionSlugMap[rawCondition];
                    const conditionKey = conditionSlug ? `weather_page.conditions.${conditionSlug}` : 'weather_page.conditions.partly_cloudy';
                    if (dayIndex === 0) {
                        grouped[key].now = {
                            temp: Number(r['tempHigh'] ?? 0),
                            icon: String(r['icon'] ?? '⛅'),
                            conditionKey,
                            humidity: Number(r['humidity'] ?? 0),
                            wind: Number(r['windKmh'] ?? 0),
                            rain: Number(r['rainChance'] ?? 0),
                        };
                    } else if (dayIndex > 0) {
                        (dayIndices[key] ??= []).push(dayIndex);
                        const rawDay = String(r['dayLabel'] ?? '');
                        const daySlug = this.dayKeyMap[rawDay];
                        grouped[key].days.push({
                            dayKey: daySlug ? `weather_page.days.${daySlug}` : rawDay,
                            icon: String(r['icon'] ?? '⛅'),
                            conditionKey,
                            high: Number(r['tempHigh'] ?? 0),
                            low: Number(r['tempLow'] ?? 0),
                            rain: Number(r['rainChance'] ?? 0),
                        });
                    }
                }
                for (const [key, g] of Object.entries(grouped)) {
                    const idx = dayIndices[key] ?? [];
                    g.days = g.days.map((d, i) => ({ d, i: idx[i] ?? i })).sort((a, b) => a.i - b.i).map((x) => x.d);
                }
                const complete = Object.keys(grouped).filter((k) => grouped[k].days.length > 0);
                if (complete.length > 0) { this.weather = grouped; if (!this.weather[this.city]) this.city = complete[0]; }
            })
            .catch(() => { /* API not available — keep built-in data */ });
    }

    get activeCity(): CityOption { return this.cities.find((c) => c.key === this.city) ?? this.cities[0]; }
    get current(): CityWeather { return this.weather[this.city] ?? this.weather['yaounde']; }
    setCity(key: string): void { this.city = key; }
}